"use client";

import { useMemo } from "react";
import { useMemoizedFn } from "ahooks";
import { useAgentReportList } from "./useAgentReportList";
import { useUserFavoriteAssets } from "./useUserFavoriteAssets";
import { AGENT_IDS_MAP, STRONG_SIGNAL_LEVELS } from "@/src/components/TA/const";
import { TAListType, TAFilterOption } from "@/src/components/TA/types";
import {
  AgentReport,
  AgentReportLevelEnum,
} from "@/src/features/api/memberApi";
import { MenuSearchModalCreateAble } from "@/src/components/Menu/MenuSearchModal";

/** 展示用列表项：在 AgentReport 基础上保证 id 为 string（拼接 agent_id 防重复） */
export type TADisplayItem = AgentReport & { id: string };

export interface UseTASectionDataParams {
  type: (typeof TAListType)[keyof typeof TAListType];
  filterOption?: TAFilterOption;
  /** 仅展示 Strong Buy / Strong Sell */
  filterStrongSignalsOnly?: boolean;
}

export interface UseTASectionDataResult {
  /** 筛选并拼接 id 后的展示列表，TAList/TAGrid 共用 */
  displayData: TADisplayItem[];
  /** 接口原始列表，用于 rawApiHasData 等 */
  data: AgentReport[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  /** 当前市场（美股/港股/Crypto）是否有关注资产 */
  hasAssetsForCurrentMarket: boolean;
  /** 接口原始返回是否有数据 */
  rawApiHasData: boolean;
  /** 打开探索弹窗（Watchlist 空态用） */
  handleExploreNow: () => Promise<void>;
}

/**
 * TA 列表/网格共用：请求参数、数据筛选、空态相关状态。
 * TAList 与 TAGrid 只负责 UI 与分页触发，数据逻辑统一由此 hook 提供。
 */
export function useTASectionData(
  params: UseTASectionDataParams,
): UseTASectionDataResult {
  const { type, filterOption, filterStrongSignalsOnly = false } = params;

  const queryParams = useMemo(
    () => ({
      agent_ids: AGENT_IDS_MAP[type],
      market: type === TAListType.Crypto ? undefined : type,
      levels: [AgentReportLevelEnum.HIGH],
      last_24h: true,
      asset_only: filterOption === "Watchlist",
      "enrich.enable": true,
      "enrich.with_asset.enable": true,
      "enrich.with_asset.with_price": true,
    }),
    [type, filterOption],
  );

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useAgentReportList({
      params: queryParams,
      allowUnauthenticated: filterOption === "All",
    });

  const { hasCryptoAssets, hasUSStocks, hasHKStocks } = useUserFavoriteAssets();

  const hasAssetsForCurrentMarket =
    type === TAListType.Crypto
      ? hasCryptoAssets
      : type === TAListType.US_Market
        ? hasUSStocks
        : hasHKStocks;

  const handleExploreNow = useMemoizedFn(async () => {
    await MenuSearchModalCreateAble({
      type: "portfolio",
      defaultRecommendedTab: type === TAListType.Crypto ? "crypto" : "stock",
    });
  });

  const displayData = useMemo(() => {
    let list = (data ?? []).filter(
      (item): item is AgentReport =>
        !!item && !!item.id && typeof item.created === "number",
    );

    if (filterStrongSignalsOnly) {
      list = list.filter((item) => {
        return (
          !!item.content?.level &&
          STRONG_SIGNAL_LEVELS.includes(
            item.content.level as (typeof STRONG_SIGNAL_LEVELS)[number],
          )
        );
      });
    }

    return list.map((item) => ({
      ...item,
      id: item.id + "-" + item.agent_id,
    })) as TADisplayItem[];
  }, [data, filterStrongSignalsOnly]);

  const rawApiHasData = (data ?? []).length > 0;

  return {
    displayData,
    data: data ?? [],
    isLoading,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage: isFetchingNextPage ?? false,
    fetchNextPage,
    hasAssetsForCurrentMarket,
    rawApiHasData,
    handleExploreNow,
  };
}
